import { Provider } from '@repo/api-gw-sdk';

import type { FieldConfig } from './fieldValidation';

// ---------- Patterns ----------
const guidPattern =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const guidRegex = new RegExp(`^${guidPattern}$`, 'i');

// Embed the body pattern where needed
const managedIdentityResourceIdRegex = new RegExp(
  `^/subscriptions/${guidPattern}/resourceGroups/[^/]+/providers/Microsoft\\.ManagedIdentity/userAssignedIdentities/[^/]+$`,
  'i'
);
const azureStorageAccountResourceIdRegex = new RegExp(
  `^/subscriptions/${guidPattern}/resourceGroups/[^/]+/providers/Microsoft\\.Storage/storageAccounts/[a-z0-9]{3,24}$`,
  'i'
);
const diskEncryptionSetResourceIdRegex = new RegExp(
  `^/subscriptions/${guidPattern}/resourceGroups/[^/]+/providers/Microsoft\\.Compute/diskEncryptionSets/[^/]+$`,
  'i'
);
const keyVaultResourceIdRegex = new RegExp(
  `^/subscriptions/${guidPattern}/resourceGroups/[^/]+/providers/Microsoft\\.KeyVault/vaults/[a-z][a-z0-9-]{0,22}[a-z0-9]$`,
  'i'
);

interface CloudFieldValidator {
  pattern: RegExp;
  message: (value: string) => string;
  placeholder: string;
}

// ---------- Central config ----------
const cloudFieldValidators = {
  [Provider.Azure]: {
    tenantId: {
      pattern: guidRegex,
      message: () =>
        'Must be a tenant ID GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 8bcd6517-b18c-571f-9bff-61090439f4f9',
    },
    // The Microsoft admin-consent endpoint accepts either a tenant GUID or a
    // verified domain (e.g. contoso.onmicrosoft.com) in the /{tenant}/ path.
    tenantIdOrDomain: {
      pattern: new RegExp(
        `^(${guidPattern}|[a-zA-Z0-9-]+(\\.[a-zA-Z0-9-]+)+)$`,
        'i'
      ),
      message: () =>
        'Must be a tenant ID GUID (8-4-4-4-12 hex digits) or a verified domain, e.g. contoso.onmicrosoft.com.',
      placeholder: 'Example: 8bcd6517-b18c-571f-9bff-61090439f4f9',
    },
    subscriptionId: {
      pattern: guidRegex,
      message: () =>
        'Must be a subscription ID GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 6f20504e-f3b2-5313-8e59-2df489d63a8d',
    },
    applicationId: {
      pattern: guidRegex,
      message: () =>
        'Must be an application (client) GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: 22c2c01c-8a37-5938-8c5a-70a0a8d151c9',
    },
    servicePrincipalId: {
      pattern: guidRegex,
      message: () =>
        'Must be a service principal GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: acb6fcee-0160-551f-944c-d66e88248db3',
    },
    objectId: {
      pattern: guidRegex,
      message: () =>
        'Must be an Azure AD object GUID in the form 8-4-4-4-12 hex digits, e.g. 12345678-90ab-cdef-1234-567890abcdef.',
      placeholder: 'Example: ab68ecfb-a022-5d84-8ff1-83b496bb7b8f',
    },
    managedIdentityResourceId: {
      pattern: managedIdentityResourceIdRegex,
      message: () =>
        'Must be a full Azure resource ID for a user-assigned managed identity.',
      placeholder:
        'Example: /subscriptions/870d...50b4/resourceGroups/my-rg/providers/Microsoft.ManagedIdentity/userAssignedIdentities/my-identity',
    },
    resourceGroupName: {
      pattern: /^[a-zA-Z0-9._()-]+$/,
      message: () =>
        'Must be a valid Azure resource group name using letters, digits, periods, underscores, hyphens, and parentheses.',
      placeholder: 'Example: my-resource-group',
    },
    managementGroupId: {
      pattern: /^[a-zA-Z0-9][a-zA-Z0-9_\-().]{0,89}(?<!\.)$/,
      message: () =>
        'Must be a valid Azure management group ID: 1–90 characters, letters, digits, hyphens, underscores, periods, and parentheses. Must start with a letter or digit and cannot end with a period.',
      placeholder: 'Example: my-management-group',
    },
    keyVaultUrl: {
      pattern: /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/?$/i,
      message: () =>
        'Must be a valid Azure Key Vault URL, e.g. https://my-vault.vault.azure.net/',
      placeholder: 'Example: https://my-vault.vault.azure.net/',
    },
    secretUrl: {
      pattern:
        /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/secrets\/[a-zA-Z0-9-]+(\/[a-zA-Z0-9]+)?\/?$/i,
      message: () =>
        'Must be a valid Azure Key Vault secret URL, e.g. https://my-vault.vault.azure.net/secrets/my-secret/version.',
      placeholder:
        'Example: https://my-vault.vault.azure.net/secrets/my-secret/version',
    },
    keyVaultKeyUri: {
      pattern:
        /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/keys\/[a-zA-Z0-9-]+\/?$/i,
      message: () =>
        'Must be a versionless Azure Key Vault key URI, e.g. https://my-vault.vault.azure.net/keys/my-key. A specific key version is not supported so Azure can auto-rotate the key.',
      placeholder:
        'Example: https://my-vault.vault.azure.net/keys/my-encryption-key',
    },
    keyVaultResourceId: {
      pattern: keyVaultResourceIdRegex,
      message: () => 'Must be a full Azure resource ID for a Key Vault.',
      placeholder:
        'Example: /subscriptions/870d...50b4/resourceGroups/my-rg/providers/Microsoft.KeyVault/vaults/my-vault',
    },
    diskEncryptionSetResourceId: {
      pattern: diskEncryptionSetResourceIdRegex,
      message: () =>
        'Must be a full Azure resource ID for a disk encryption set.',
      placeholder:
        'Example: /subscriptions/870d...50b4/resourceGroups/my-rg/providers/Microsoft.Compute/diskEncryptionSets/my-des',
    },
    storageAccountResourceId: {
      pattern: azureStorageAccountResourceIdRegex,
      message: () => 'Must be a full Azure resource ID for a storage account.',
      placeholder:
        'Example: /subscriptions/870d...50b4/resourceGroups/my-rg/providers/Microsoft.Storage/storageAccounts/mystorage',
    },
    vmName: {
      pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/,
      message: () =>
        'Must be 1-80 characters and start with a letter or digit. Letters, digits, periods, underscores, and hyphens are allowed.',
      placeholder: 'Example: restored-vm',
    },
    sqlDatabaseName: {
      pattern:
        /^(?![ .])(?:[^<>*%&:\\/?\s]|[^<>*%&:\\/?\s][^<>*%&:\\/?]{0,126}[^<>*%&:\\/?\s.])$/,
      message: () =>
        'Must be 1-128 characters and cannot contain < > * % & : \\ / ? or start/end with whitespace or a period.',
      placeholder: 'Example: restored_database',
    },
  },

  [Provider.Aws]: {
    accountId: {
      pattern: /^[0-9]{12}$/,
      message: () => 'Must be a 12-digit AWS account ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    organizationId: {
      pattern: /^o-[a-z0-9]{10,32}$/,
      message: () => 'Must be an AWS organization ID like o-xxxxxxxxxxxx.',
      placeholder: 'Example: o-a1b2c3d4e5',
    },
    organizationUnitId: {
      // Technically it can be also a spaceseparated list of OUs, but we there's no ready regex for that
      pattern: /^(ou-[a-z0-9]{4,32}-[a-z0-9]{8,32}|r-[a-z0-9]{4,32})$/,
      message: () =>
        'Must be the organization root ID like r-xxxxxxxxxx or an AWS organizational unit ID like ou-xxxx-xxxxxxxx.',
      placeholder: 'Example: ou-ab12-mvwst22c',
    },
    arn: {
      pattern: /^arn:[^:]+:[^:]*:[^:]*:[0-9]*:.*$/,
      message: () =>
        'Must be a valid ARN, e.g. arn:aws:iam::123456789012:role/MyRole.',
      placeholder: 'Example: arn:aws:iam::123456789012:role/MyRole',
    },
    roleArn: {
      pattern: /^arn:aws:iam::[0-9]{12}:role\/[A-Za-z0-9+=,.@_\-/]+$/,
      message: () =>
        'Must be a valid IAM role ARN, e.g. arn:aws:iam::123456789012:role/MyRole.',
      placeholder: 'Example: arn:aws:iam::123456789012:role/MyRole',
    },
    userArn: {
      pattern: /^arn:aws:iam::[0-9]{12}:user\/[A-Za-z0-9+=,.@_\-/]+$/,
      message: () =>
        'Must be a valid IAM user ARN, e.g. arn:aws:iam::123456789012:user/MyUser.',
      placeholder: 'Example: arn:aws:iam::123456789012:user/MyUser',
    },
    iamPrincipalArn: {
      pattern: /^arn:aws:iam::[0-9]{12}:(root|user\/[^\s]+|role\/[^\s]+)$/,
      message: () =>
        'Must be a valid IAM principal ARN, e.g. arn:aws:iam::123456789012:role/MyRole.',
      placeholder: 'arn:aws:iam::123456789012:role/MyRole',
    },
    kmsKeyArn: {
      pattern:
        /^arn:aws:kms:[a-z0-9-]+:[0-9]{12}:(key\/(mrk-)?[0-9a-f-]+|alias\/[a-zA-Z0-9/_-]+)$/,
      message: () =>
        'Must be a valid KMS key or alias ARN, e.g. arn:aws:kms:us-east-1:123456789012:key/12345678-90ab-cdef-1234-567890abcdef or arn:aws:kms:us-east-1:123456789012:alias/my-key-alias.',
      placeholder:
        'Example: arn:aws:kms:us-east-1:123456789012:key/12345678-90ab-cdef-1234-567890abcdef',
    },
    ami: {
      pattern: /^ami-[0-9a-f]{8,17}$/,
      message: () => 'Must be a valid AMI ID, e.g. ami-0abcdef1234567890.',
      placeholder: 'Example: ami-0abcdef1234567890',
    },
    amiName: {
      // See https://docs.aws.amazon.com/AWSEC2/latest/APIReference/API_RegisterImage.html#:~:text=Name,Yes
      pattern: /^[a-zA-Z0-9()[\] .\-/'@_]{3,128}$/,
      message: () =>
        "AMI names must be between 3 and 128 characters long, and may contain letters, numbers, spaces, '(', ')', '[', ']', '.', '-', '/', '@', '_', and single quotes.",
      placeholder: 'Example: my-ami-name',
    },
    amiDescription: {
      pattern: /^$|^[\s\S]{1,255}$/,
      message: () => 'Must be 255 characters or fewer.',
      placeholder: 'Example: Restored image for testing',
    },
    keyspacesKeyspaceName: {
      pattern: /^[a-zA-Z_][a-zA-Z0-9_]{0,47}$/,
      message: () =>
        'Must be 1–48 characters: letters, digits, and underscores. Must start with a letter or underscore.',
      placeholder: 'Example: my_keyspace',
    },
    keyspacesTableName: {
      pattern: /^[a-zA-Z_][a-zA-Z0-9_]{0,47}$/,
      message: () =>
        'Must be 1–48 characters: letters, digits, and underscores. Must start with a letter or underscore.',
      placeholder: 'Example: my_table',
    },
    rdsPassword: {
      pattern: /^[^\s/\\@"']{8,41}$/,
      message: () =>
        'Must be 8–41 characters. Cannot contain spaces, /, \\, @, ", or single quotes.',
      placeholder: 'Example: aBcDe12345-_!%&',
    },
    cloudwatchLogGroupArn: {
      pattern:
        /^arn:aws:logs:[a-z0-9-]+:[0-9]{12}:log-group:[A-Za-z0-9._/#-]+(:\*)?$/,
      message: () =>
        'Must be a valid CloudWatch log group ARN, e.g. arn:aws:logs:us-east-1:123456789012:log-group:my-log-group.',
      placeholder:
        'Example: arn:aws:logs:us-east-1:123456789012:log-group:my-log-group',
    },
    openSearchDomainArn: {
      pattern: /^arn:aws:es:[a-z0-9-]+:[0-9]{12}:domain\/[a-z][a-z0-9-]{2,27}$/,
      message: () =>
        'Must be a valid OpenSearch domain ARN, e.g. arn:aws:es:us-east-1:123456789012:domain/my-domain.',
      placeholder:
        'Example: arn:aws:es:us-east-1:123456789012:domain/my-domain',
    },
    ampWorkspaceArn: {
      pattern:
        /^arn:aws:aps:[a-z0-9-]+:[0-9]{12}:workspace\/ws-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
      message: () =>
        'Must be a valid Amazon Managed Prometheus workspace ARN, e.g. arn:aws:aps:us-east-1:123456789012:workspace/ws-12345678-1234-1234-1234-123456789012.',
      placeholder:
        'Example: arn:aws:aps:us-east-1:123456789012:workspace/ws-12345678-1234-1234-1234-123456789012',
    },
    s3BucketName: {
      pattern: /^(?!xn--)(?!.*\.\.)[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/,
      message: () =>
        'Must be a valid S3 bucket name (3–63 chars, lowercase letters, digits, dots, hyphens).',
      placeholder: 'Example: my-bucket',
    },
    s3KeyPrefix: {
      pattern: /^[A-Za-z0-9!_.*'()/-][A-Za-z0-9!_.*'()/-]*$/,
      message: () =>
        'Must be a valid S3 key prefix (no leading slash or whitespace).',
      placeholder: 'Example: path/to/objects',
    },
    rdsInstanceIdentifier: {
      pattern: /^(?!.*--)[A-Za-z][A-Za-z0-9-]{0,62}(?<!-)$/,
      message: () =>
        'Must be 1-63 characters, start with a letter, contain only letters, digits, and hyphens, and cannot end with a hyphen or contain consecutive hyphens.',
      placeholder: 'Example: restored-database',
    },
    dynamodbTableName: {
      pattern: /^[A-Za-z0-9_.-]{3,255}$/,
      message: () =>
        'Must be 3-255 characters and contain only letters, digits, underscores, periods, and hyphens.',
      placeholder: 'Example: restored-table',
    },
    vpcEndpointId: {
      pattern: /^vpce-[a-z0-9]+$/,
      message: () =>
        'Must be a valid VPC endpoint ID, e.g. vpce-0123456789abcdef0.',
      placeholder: 'Example: vpce-0123456789abcdef0',
    },
    fsxFileSystemName: {
      pattern: /^[A-Za-z0-9 _.:/+=@-]{1,256}$/,
      message: () =>
        'Must be 1-256 characters and contain only letters, digits, spaces, underscores, periods, colons, slashes, plus signs, equals signs, @, and hyphens.',
      placeholder: 'Example: restored-file-system',
    },
    fsxOntapVolumeName: {
      pattern: /^[A-Za-z_][A-Za-z0-9_]{0,202}$/,
      message: () =>
        'Must be 1-203 characters, start with a letter or underscore, and contain only letters, digits, and underscores.',
      placeholder: 'Example: restored_volume',
    },
    fsxJunctionPath: {
      pattern: /^(?=.{1,255}$)(?:\/[^/\s]+)+$/,
      message: () =>
        'Must start with /, cannot contain empty path segments, and cannot exceed 255 characters.',
      placeholder: 'Example: /restored-volume',
    },
  },

  [Provider.Gcp]: {
    projectId: {
      pattern: /^[a-z][a-z0-9-]{5,29}$/,
      message: () =>
        'Must be a valid GCP project ID: 6–30 chars, lowercase letters, digits, and hyphens, starting with a letter, e.g. my-project-123.',
      placeholder: 'Example: my-project-123',
    },
    projectNumber: {
      pattern: /^[0-9]{6,30}$/,
      message: () => 'Must be a numeric project number, e.g. 1234567890123.',
      placeholder: 'Example: 1234567890123',
    },
    organizationId: {
      pattern: /^[0-9]{6,30}$/,
      message: () => 'Must be a numeric organization ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    folderId: {
      pattern: /^[0-9]{6,30}$/,
      message: () => 'Must be a numeric folder ID, e.g. 123456789012.',
      placeholder: 'Example: 123456789012',
    },
    serviceAccountEmail: {
      pattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
      message: () =>
        'Must be a valid service account email, e.g. my-sa@my-project.iam.gserviceaccount.com.',
      placeholder: 'Example: my-sa@my-project.iam.gserviceaccount.com',
    },
    GcpIamPrincipal: {
      pattern:
        /^(?:allUsers|allAuthenticatedUsers|(?:(?:user|group|serviceAccount):[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{1,59})|domain:(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{1,59})|project(?:Owner|Editor|Viewer):[a-z][a-z0-9-]{4,28}[a-z0-9]|serviceAccount:[a-z][a-z0-9-]{4,28}[a-z0-9]\.svc\.id\.goog\[[^\s/\]]+\/[^\s/\]]+\]|principalSet:\/\/cloudresourcemanager\.googleapis\.com\/(?:projects|folders|organizations)\/[0-9]+\/type\/ServiceAccount|principal:\/\/iam\.googleapis\.com\/locations\/global\/workforcePools\/[^/\s]+\/subject\/[^/\s]+|principalSet:\/\/iam\.googleapis\.com\/locations\/global\/workforcePools\/[^/\s]+\/(?:group\/[^/\s]+|attribute\.[A-Za-z0-9_.-]+\/[^/\s]+|\*)|principal:\/\/iam\.googleapis\.com\/projects\/[0-9]+\/locations\/global\/workloadIdentityPools\/[^/\s]+\/subject\/[^/\s]+|principalSet:\/\/iam\.googleapis\.com\/projects\/[0-9]+\/locations\/global\/workloadIdentityPools\/[^/\s]+\/(?:group\/[^/\s]+|attribute\.[A-Za-z0-9_.-]+\/[^/\s]+|\*)|principal:\/\/iam\.googleapis\.com\/projects\/[0-9]+\/locations\/global\/workloadIdentityPools\/[a-z][a-z0-9-]{4,28}[a-z0-9]\.svc\.id\.goog\/(?:subject\/ns\/[^/\s]+\/sa\/[^/\s]+|kubernetes\.serviceaccount\.uid\/[^/\s]+)|principalSet:\/\/iam\.googleapis\.com\/projects\/[0-9]+\/locations\/global\/workloadIdentityPools\/[a-z][a-z0-9-]{4,28}[a-z0-9]\.svc\.id\.goog\/(?:namespace\/[^/\s]+|kubernetes\.cluster\/https:\/\/container\.googleapis\.com\/v1\/projects\/[a-z][a-z0-9-]{4,28}[a-z0-9]\/locations\/[^/\s]+\/clusters\/[^/\s]+)|principal:\/\/[A-Za-z0-9][A-Za-z0-9.-]*\/resources\/[A-Za-z0-9][A-Za-z0-9.-]*\/[^\s]+|principalSet:\/\/[A-Za-z0-9][A-Za-z0-9.-]*\/(?:\*|attribute\.platformContainer\/aiplatform\/projects\/[0-9]+)|principal:\/\/parametermanager\.googleapis\.com\/projects\/[0-9]+\/uid\/locations\/global\/parameters\/[^/\s]+|principal:\/\/iam\.googleapis\.com\/projects\/[0-9]+\/name\/locations\/global\/workloadIdentityPools\/[^/\s]+|principalSet:\/\/[a-z0-9][a-z0-9.-]*\.googleapis\.com\/projects\/[0-9]+\/(?:\*|type\/[A-Za-z][A-Za-z0-9_.-]*\/\*|ancestor\.(?:name|uid)\/[A-Za-z][A-Za-z0-9_.-]*\/[^/\s]+|type\/[A-Za-z][A-Za-z0-9_.-]*\/ancestor\.(?:name|uid)\/[A-Za-z][A-Za-z0-9_.-]*\/[^/\s]+)))$/,
      message: () =>
        'Must be a valid GCP IAM principal identifier. See https://cloud.google.com/iam/docs/principal-identifiers for the supported formats.',
      placeholder: 'Example: user:alice@example.com',
    },
    kmsKeyResourceName: {
      pattern:
        /^projects\/[^/]+\/locations\/[^/]+\/keyRings\/[^/]+\/cryptoKeys\/[^/]+$/,
      message: () =>
        'Must be a valid GCP KMS key resource name, e.g. projects/my-project/locations/us-central1/keyRings/my-ring/cryptoKeys/my-key.',
      placeholder:
        'Example: projects/my-project/locations/us-central1/keyRings/my-ring/cryptoKeys/my-key',
    },
    bigQueryDatasetId: {
      pattern: /^[a-zA-Z0-9_]{1,1024}$/,
      message: () =>
        'Must be a valid BigQuery dataset ID: letters, numbers, and underscores only. No spaces or special characters like -, &, @, or %.',
      placeholder: 'Example: my_dataset_123',
    },
    bigQueryLocation: {
      pattern: /^[A-Za-z0-9-]+$/,
      message: () => 'Must be a valid BigQuery location ID.',
      placeholder: 'Example: US',
    },
    labelKey: {
      pattern: /^[a-z][a-z0-9_-]{0,62}$/,
      message: () =>
        'Must start with a lowercase letter; up to 63 chars of lowercase letters, digits, hyphens, or underscores.',
      placeholder: 'Example: env',
    },
    labelValue: {
      pattern: /^([a-z][a-z0-9_-]{0,62})?$/,
      message: () =>
        'Optional. If set, must start with a lowercase letter; up to 63 chars of lowercase letters, digits, hyphens, or underscores.',
      placeholder: 'Example: production',
    },
    tagShortName: {
      pattern: /^[a-zA-Z0-9_.-]{1,63}$/,
      message: () =>
        'Up to 63 chars of letters, digits, hyphens, underscores, or dots.',
      placeholder: 'Example: production',
    },
  },

  [Provider.MongoAtlas]: {
    projectId: {
      pattern: /^[0-9a-f]{24}$/,
      message: () =>
        'Must be a valid 24-character hex MongoDB Atlas Project ID.',
      placeholder: 'Example: 60b8e1234567890abcdef345',
    },
    orgId: {
      pattern: /^[0-9a-f]{24}$/,
      message: () =>
        'Must be a valid 24-character hex MongoDB Atlas Organization ID.',
      placeholder: 'Example: 60b8e1234567890abcdef345',
    },
    clusterName: {
      pattern: /^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/,
      message: () =>
        'Must be 1-64 characters and contain only letters, digits, and hyphens.',
      placeholder: 'Example: restored-cluster',
    },
  },
} satisfies Partial<Record<Provider, Record<string, CloudFieldValidator>>>;

type CloudFieldValidators = typeof cloudFieldValidators;
type CloudField<P extends keyof CloudFieldValidators> =
  keyof CloudFieldValidators[P];

interface CloudFieldValidationOptions {
  /**
   * Whether the field is required. If false, empty values will pass validation.
   * Default: false
   */
  required?: boolean;
  /**
   * Whether to allow leading/trailing whitespace. If false, the value will be validated as-is.
   * If true, leading/trailing whitespace will be trimmed before validation.
   * Note: Whitespace in the middle of the string is always validated by the pattern.
   * Default: false
   */
  allowWhitespace?: boolean;
}

/**
 * Get a field configuration object with validation and placeholder text
 * for account-level identifiers: tenant, subscription, account, org,
 * project, folder, application, servicePrincipal, etc.
 */
export function getCloudFieldConfig<P extends keyof CloudFieldValidators>(
  provider: P,
  field: CloudField<P>,
  options: CloudFieldValidationOptions = {}
): FieldConfig {
  const allValidators: Partial<
    Record<Provider, Record<string, CloudFieldValidator>>
  > = cloudFieldValidators;
  const rule = allValidators[provider]?.[String(field)];
  if (!rule) {
    throw new Error(
      `Unknown field '${String(field)}' for provider '${provider}'`
    );
  }
  const { required = false, allowWhitespace = false } = options;

  return {
    validation: {
      validateInput: (value: string) => {
        const testValue = allowWhitespace ? value.trim() : value;
        // If empty (after trimming if allowed), only pass if not required
        if (!testValue) {
          return !required;
        }
        return rule.pattern.test(testValue);
      },
      errorMessage: rule.message,
    },
    placeholder: rule.placeholder,
  };
}
